<script lang="ts">
  import { cue } from '../audio/sound';
  import { t } from '../i18n';
  import { SPEEDS, playback, setSpeed, speedIndex } from '../state/stores';

  // Time throttle (S2-05): a segmented bar graph with one detent per speed (style B). It is an ARIA slider: drag or
  // tap a segment, or use the arrow keys / Home / End while focused.
  const index = $derived(speedIndex($playback.speed));
  const current = $derived(SPEEDS[index]!);

  let bar: HTMLDivElement;
  let dragging = false;

  function select(i: number): void {
    const s = SPEEDS[Math.min(Math.max(i, 0), SPEEDS.length - 1)]!;
    if (s.value !== $playback.speed) {
      setSpeed(s.value);
      cue('detent');
    }
  }

  function indexAt(clientX: number): number {
    const r = bar.getBoundingClientRect();
    return Math.floor(((clientX - r.left) / r.width) * SPEEDS.length);
  }

  function onPointerDown(e: PointerEvent): void {
    dragging = true;
    bar.setPointerCapture(e.pointerId);
    select(indexAt(e.clientX));
  }
  function onPointerMove(e: PointerEvent): void {
    if (dragging) select(indexAt(e.clientX));
  }
  function onPointerUp(e: PointerEvent): void {
    dragging = false;
    if (bar.hasPointerCapture(e.pointerId)) bar.releasePointerCapture(e.pointerId);
  }

  function onKeydown(e: KeyboardEvent): void {
    const step: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 };
    if (e.key in step) select(index + step[e.key]!);
    else if (e.key === 'Home') select(0);
    else if (e.key === 'End') select(SPEEDS.length - 1);
    else return;
    // Keep the global date shortcuts (←/→) from also firing.
    e.preventDefault();
    e.stopPropagation();
  }
</script>

<div class="throttle">
  <span class="caps-label" aria-hidden="true">{$t('time.throttleShort')}</span>
  <div
    bind:this={bar}
    class="bar"
    role="slider"
    tabindex="0"
    aria-label={$t('time.throttle')}
    aria-valuemin={0}
    aria-valuemax={SPEEDS.length - 1}
    aria-valuenow={index}
    aria-valuetext={$t(`speed.${current.id}`)}
    data-testid="throttle"
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointercancel={onPointerUp}
    onkeydown={onKeydown}
  >
    {#each SPEEDS as s, i (s.id)}
      <span class="seg" class:on={i < index} class:current={i === index}>{$t(`speedShort.${s.id}`)}</span>
    {/each}
  </div>
</div>

<style>
  .throttle {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .bar {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 4px;
    min-height: var(--touch);
    align-items: center;
    touch-action: none;
    cursor: pointer;
    user-select: none;
  }
  .seg {
    height: 24px;
    display: grid;
    place-items: center;
    background: var(--rule);
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    transition:
      background-color var(--dur-fast) var(--ease),
      color var(--dur-fast) var(--ease);
  }
  .seg.on {
    background: var(--accent);
    color: var(--on-accent);
  }
  .seg.current {
    background: var(--signal);
    color: var(--on-signal);
  }
  .bar:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 3px;
  }
</style>
